import React, { FC, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  position?: 'top' | 'bottom';
  delay?: number;
  translateX?: number;
  multiLine?: boolean;
  noPlague?: boolean;
}

const Tooltip: FC<TooltipProps> = ({
  content,
  children,
  position = 'bottom',
  delay = 200,
  translateX = 0,
  multiLine = false,
  noPlague = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timeout = useRef<any>();

  useEffect(() => {
    const updatePosition = () => {
      if (wrapperRef.current && tooltipRef.current) {
        const elementRect = wrapperRef.current.getBoundingClientRect();
        const tooltipHeight = tooltipRef.current.offsetHeight;
        const tooltipWidth = tooltipRef.current.offsetWidth;

        let left = elementRect.left + elementRect.width / 2 + translateX;
        let top =
          position === 'top' ? elementRect.top - tooltipHeight - 10 : elementRect.bottom + 10;

        const halfTooltipWidth = tooltipWidth / 2;
        const screenWidth = window.innerWidth;
        if (left + halfTooltipWidth > screenWidth) {
          left = screenWidth - halfTooltipWidth - 10;
        }
        if (left - halfTooltipWidth < 0) {
          left = halfTooltipWidth + 10;
        }

        setCoords({ top, left });
      }
    };

    if (visible) {
      updatePosition();
    }
  }, [visible, position, translateX]);

  const showTooltip = () => {
    timeout.current = setTimeout(() => {
      setVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    clearTimeout(timeout.current);
    setVisible(false);
  };

  return (
    <div
      className="tt-wrapper"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      ref={wrapperRef}
    >
      {children}
      {createPortal(
        visible && (
          <div
            ref={tooltipRef}
            className={`tt ${position} ${noPlague ? 'tt-no-plague' : ''} ${multiLine ? 'tt-multi-line' : ''
              }`}
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
            }}
          >
            <div className="tt-content">{content}</div>
          </div>
        ),
        document.body
      )}
    </div>
  );
};

export default Tooltip;
